/**
 * HOME 進行中ホイールのカード ⇄ Preview ページのメディア表示位置の
 * 「共有エレメント遷移」(palmer-dinnerware.com 風)。
 *
 * 仕組み（クローン FLIP 方式）:
 *  1. 遷移直前に起点要素の矩形を測り、サムネイルのクローンを document.body
 *     直下へ position: fixed で浮かせる（Preview の ModalOverlay(z=999) より
 *     上の z=1000）。
 *  2. 呼び出し側はそのまま navigate する。クローンは React ツリーの外の
 *     素の DOM + WAAPI で動くため、遷移元が Activity で hidden になっても
 *     アニメーションは中断されない。
 *  3. 遷移先の着地目標がマウントされ矩形が安定したら、クローンをそこへ
 *     移動・リサイズして着地させ、クロスフェードで実表示に引き継ぐ。
 *  4. 着地目標が見つからないままタイムアウトした場合は、その場でフェード
 *     アウトして通常遷移にフォールバックする。
 *
 * 移動は top/left/width/height ではなく transform（translate + scale）で行う。
 * 遷移中はページのマウント/アンマウントでメインスレッドが数百 ms 詰まるため、
 * レイアウトプロパティのアニメーションだとコマ落ちして「静止 → 瞬間移動」に
 * 見える。transform はコンポジタ側で合成されるので影響を受けない。
 * 縦横比の変化（scale の非等倍）による画像の歪みは、内側 img への逆スケールで
 * 打ち消し、objectFit: cover 相当の「切り抜きが変わる」見え方を保つ。
 *
 * 方向:
 *  - 往路 startSharedMediaTransition: カード → Preview のメディア表示位置
 *    （`data-preview-media-landing` 属性を持つ要素、またはその中の video/img）
 *  - 復路 startSharedMediaReturnTransition: Preview のメディア表示位置 →
 *    ホイールカード（`data-wheel-shot-media="<shotId>"` を持つ要素）
 */

export const PREVIEW_MEDIA_LANDING_ATTR = "data-preview-media-landing";
/** ホイール側の起点/着地要素（ショットカード・待機中サムネイル）を示す属性。 */
export const WHEEL_SHOT_MEDIA_ATTR = "data-wheel-shot-media";

/** 着地目標の出現を待つ上限。遷移先の描画が長引いたら諦める。 */
const MAX_WAIT_MS = 2500;
/** 矩形がこのフレーム数連続で安定したら「レイアウト確定」とみなす。 */
const STABLE_FRAMES = 3;
/** 矩形の安定判定のしきい値(px)。 */
const STABLE_EPSILON_PX = 1;
/** 着地目標として認める最小サイズ(px)。 */
const MIN_TARGET_SIZE_PX = 40;
/** 移動・リサイズの所要時間(ms)。 */
const MOVE_DURATION_MS = 480;
/** 着地後のクロスフェード時間(ms)。 */
const FADE_DURATION_MS = 240;
/** 何かが詰まっても必ずクローンを破棄する安全弁(ms)。 */
const HARD_TIMEOUT_MS = 6000;

const MOVE_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

let disposeActive: (() => void) | null = null;

/** 要素の実際の角丸(px)。クローンの角丸補間は起点/着地の実スタイルに合わせる。 */
const elementBorderRadiusPx = (el: HTMLElement): number => {
  const value = parseFloat(getComputedStyle(el).borderTopLeftRadius);
  return Number.isFinite(value) ? value : 0;
};

const rectsAlmostEqual = (a: DOMRect, b: DOMRect): boolean =>
  Math.abs(a.top - b.top) < STABLE_EPSILON_PX &&
  Math.abs(a.left - b.left) < STABLE_EPSILON_PX &&
  Math.abs(a.width - b.width) < STABLE_EPSILON_PX &&
  Math.abs(a.height - b.height) < STABLE_EPSILON_PX;

const rectCenterInViewport = (rect: DOMRect): boolean => {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  return cx >= 0 && cx <= window.innerWidth && cy >= 0 && cy <= window.innerHeight;
};

/** Preview 側の着地/起点要素。コンテナより実メディア（video/img）を優先する。 */
const resolvePreviewMediaElement = (): HTMLElement | null => {
  const landing = document.querySelector<HTMLElement>(
    `[${PREVIEW_MEDIA_LANDING_ATTR}]`,
  );
  if (!landing) return null;

  const media = landing.querySelector<HTMLElement>("video, img");
  const candidates = media ? [media, landing] : [landing];
  for (const el of candidates) {
    const rect = el.getBoundingClientRect();
    if (rect.width >= MIN_TARGET_SIZE_PX && rect.height >= MIN_TARGET_SIZE_PX) {
      return el;
    }
  }
  return null;
};

/** ホイール側（カード / 待機中サムネイル）の着地/起点要素。画面外なら不採用。 */
const resolveWheelShotElement = (shotId: string): HTMLElement | null => {
  const el = document.querySelector<HTMLElement>(
    `[${WHEEL_SHOT_MEDIA_ATTR}="${shotId}"]`,
  );
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width < MIN_TARGET_SIZE_PX || rect.height < MIN_TARGET_SIZE_PX) {
    return null;
  }
  if (!rectCenterInViewport(rect)) return null;
  return el;
};

interface RunTransitionArgs {
  /** 遷移の起点要素。矩形と角丸はここから測る。 */
  sourceEl: HTMLElement;
  imageSrc?: string;
  /** 遷移先の着地要素（未マウントなら null）。矩形と角丸はここから測る。 */
  resolveTargetEl: () => HTMLElement | null;
}

const runTransition = ({
  sourceEl,
  imageSrc,
  resolveTargetEl,
}: RunTransitionArgs): void => {
  // 多重起動（連打）時は前のクローンを即破棄する。
  disposeActive?.();

  const sourceRect = sourceEl.getBoundingClientRect();
  const sourceRadius = elementBorderRadiusPx(sourceEl);
  if (sourceRect.width < 1 || sourceRect.height < 1) return;

  const clone = document.createElement("div");
  Object.assign(clone.style, {
    position: "fixed",
    top: `${sourceRect.top}px`,
    left: `${sourceRect.left}px`,
    width: `${sourceRect.width}px`,
    height: `${sourceRect.height}px`,
    borderRadius: `${sourceRadius}px`,
    overflow: "hidden",
    zIndex: "1000",
    pointerEvents: "none",
    background: "#f0f0f0",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.16)",
    transformOrigin: "0 0",
    willChange: "transform",
  } satisfies Partial<CSSStyleDeclaration>);

  let cloneImg: HTMLImageElement | null = null;
  if (imageSrc) {
    const img = document.createElement("img");
    img.src = imageSrc;
    img.alt = "";
    Object.assign(img.style, {
      display: "block",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transformOrigin: "50% 50%",
      willChange: "transform",
    } satisfies Partial<CSSStyleDeclaration>);
    clone.appendChild(img);
    cloneImg = img;
  }

  document.body.appendChild(clone);

  let rafId = 0;
  let hardTimer = 0;
  let disposed = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(rafId);
    window.clearTimeout(hardTimer);
    clone.remove();
    if (disposeActive === dispose) disposeActive = null;
  };
  disposeActive = dispose;

  hardTimer = window.setTimeout(() => dispose(), HARD_TIMEOUT_MS);

  const fadeOutAndDispose = () => {
    if (disposed) return;
    const fade = clone.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: FADE_DURATION_MS,
      easing: "ease-out",
      fill: "forwards",
    });
    fade.onfinish = dispose;
    fade.oncancel = dispose;
  };

  const moveToTarget = (targetEl: HTMLElement, target: DOMRect) => {
    if (disposed) return;
    const dx = target.left - sourceRect.left;
    const dy = target.top - sourceRect.top;
    const sx = target.width / sourceRect.width;
    const sy = target.height / sourceRect.height;

    // transform のみのアニメーション（コンポジタ合成でコマ落ちしない）
    const move = clone.animate(
      [
        { transform: "translate(0px, 0px) scale(1, 1)" },
        { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` },
      ],
      { duration: MOVE_DURATION_MS, easing: MOVE_EASING, fill: "forwards" },
    );

    // 内側 img は「大きい方のスケール ÷ 各軸スケール」で逆補正し、
    // 非等倍 scale による歪みを打ち消す（= cover の切り抜きが変わる見え方）。
    if (cloneImg) {
      const k = Math.max(sx, sy);
      cloneImg.animate(
        [
          { transform: "scale(1, 1)" },
          { transform: `scale(${k / sx}, ${k / sy})` },
        ],
        { duration: MOVE_DURATION_MS, easing: MOVE_EASING, fill: "forwards" },
      );
    }

    // 角丸は transform と別トラック（同一 animate に混ぜると transform の
    // コンポジタ合成が外れるため分離）。外箱が scale されるぶん、着地時に
    // 視覚上 target の角丸になるよう軸ごとに割り戻す。
    const targetRadius = elementBorderRadiusPx(targetEl);
    clone.animate(
      [
        { borderRadius: `${sourceRadius}px` },
        { borderRadius: `${targetRadius / sx}px / ${targetRadius / sy}px` },
      ],
      { duration: MOVE_DURATION_MS, easing: MOVE_EASING, fill: "forwards" },
    );

    move.onfinish = fadeOutAndDispose;
    move.oncancel = dispose;
  };

  const startedAt = performance.now();
  let lastRect: DOMRect | null = null;
  let stableCount = 0;

  const poll = () => {
    if (disposed) return;
    if (performance.now() - startedAt > MAX_WAIT_MS) {
      // 着地目標が見つからない（描画長引き・対象消滅等）→ フェードのみ。
      fadeOutAndDispose();
      return;
    }

    const targetEl = resolveTargetEl();
    const rect = targetEl ? targetEl.getBoundingClientRect() : null;
    if (targetEl && rect) {
      stableCount = lastRect && rectsAlmostEqual(rect, lastRect) ? stableCount + 1 : 1;
      lastRect = rect;
      if (stableCount >= STABLE_FRAMES) {
        moveToTarget(targetEl, rect);
        return;
      }
    } else {
      stableCount = 0;
      lastRect = null;
    }
    rafId = requestAnimationFrame(poll);
  };

  rafId = requestAnimationFrame(poll);
};

// ===== 往路: ホイールカード → Preview =====

export interface SharedMediaTransitionOptions {
  /** 遷移の起点となるカードのメディア要素。 */
  sourceEl: HTMLElement;
  /** クローンに表示するサムネイル URL。無ければ無地ブロックで代用。 */
  imageSrc?: string;
}

export const startSharedMediaTransition = ({
  sourceEl,
  imageSrc,
}: SharedMediaTransitionOptions): void => {
  if (typeof document === "undefined") return;
  runTransition({
    sourceEl,
    imageSrc,
    resolveTargetEl: resolvePreviewMediaElement,
  });
};

// ===== 復路: Preview → ホイールカード =====

export interface SharedMediaReturnTransitionOptions {
  /** 戻り先ホイールカードのショット ID。 */
  shotId: string;
  /** クローンに表示するサムネイル URL（起点から取得できない場合の指定）。 */
  imageSrc?: string;
}

/**
 * Preview を閉じる直前に呼ぶ。現在表示中のメディア（最初のスライドの
 * `data-preview-media-landing`）からホイールカードへ縮小して戻す。
 * 起点が画面外（別バリエーションへスクロール済み等）の場合は何もしない。
 */
export const startSharedMediaReturnTransition = ({
  shotId,
  imageSrc,
}: SharedMediaReturnTransitionOptions): void => {
  if (typeof document === "undefined") return;

  const sourceEl = resolvePreviewMediaElement();
  if (!sourceEl) return;
  const sourceRect = sourceEl.getBoundingClientRect();
  if (!rectCenterInViewport(sourceRect)) return;

  // 起点の実メディアからサムネイルを引き継ぐ（img: 表示中画像 / video: poster）
  let resolvedImageSrc = imageSrc;
  if (sourceEl instanceof HTMLImageElement && sourceEl.currentSrc) {
    resolvedImageSrc = sourceEl.currentSrc;
  } else if (sourceEl instanceof HTMLVideoElement && sourceEl.poster) {
    resolvedImageSrc = sourceEl.poster;
  }

  runTransition({
    sourceEl,
    imageSrc: resolvedImageSrc,
    resolveTargetEl: () => resolveWheelShotElement(shotId),
  });
};

// ===== ページ内 FLIP: レイアウト変更（コメントパネル開閉など）のメディア滑空 =====
//
// Home ⇄ Preview のクローン方式と違い、こちらはページ内でレイアウトだけが
// 変わるケース用。クローンは作らず実要素へ transform を掛けるため、再生中の
// video も途切れずそのまま動く。
//
// 使い方:
//  1. レイアウト変更前に captureMediaVisualRect で視覚矩形を控える
//  2. レイアウトを「即時」切り替える（幅などの CSS transition は無効化する。
//     transition が残っていると FLIP と二重に動いてしまう）
//  3. useLayoutEffect（DOM 更新後・paint 前）から runMediaLayoutFlip を呼ぶ

/** ページ内 FLIP の対象ボックス（Preview 中央カラムのメディア外箱）を示す属性。 */
export const PREVIEW_MEDIA_FLIP_ATTR = "data-preview-media-flip";

let disposeActiveFlip: (() => void) | null = null;

/**
 * object-fit: contain を考慮した「実際にメディアが見えている」矩形。
 * 動画がレターボックス表示（要素箱の中で余白付き）になっていても、
 * 目で追う映像部分の矩形を返す。intrinsic サイズ不明時は要素矩形で代用。
 */
const mediaVisualRect = (el: HTMLElement): DOMRect => {
  const rect = el.getBoundingClientRect();
  let iw = 0;
  let ih = 0;
  if (el instanceof HTMLVideoElement) {
    iw = el.videoWidth;
    ih = el.videoHeight;
  } else if (el instanceof HTMLImageElement) {
    iw = el.naturalWidth;
    ih = el.naturalHeight;
  }
  if (iw < 1 || ih < 1 || rect.width < 1 || rect.height < 1) return rect;
  if (getComputedStyle(el).objectFit !== "contain") return rect;

  const fit = Math.min(rect.width / iw, rect.height / ih);
  const w = iw * fit;
  const h = ih * fit;
  return new DOMRect(
    rect.left + (rect.width - w) / 2,
    rect.top + (rect.height - h) / 2,
    w,
    h,
  );
};

/** FLIP 対象ボックス内の実メディア（video/img）の視覚矩形。 */
export const captureMediaVisualRect = (flipBox: HTMLElement): DOMRect => {
  const media = flipBox.querySelector<HTMLElement>("video, img");
  return mediaVisualRect(media ?? flipBox);
};

/**
 * root 配下の FLIP 対象ボックスのうち画面内にあるものを返す。
 * テイク横カルーセルのように複数並ぶ場合は、見えているものを採用する。
 */
export const resolveMediaFlipBox = (root: ParentNode): HTMLElement | null => {
  const boxes = root.querySelectorAll<HTMLElement>(`[${PREVIEW_MEDIA_FLIP_ATTR}]`);
  for (const box of boxes) {
    if (rectCenterInViewport(box.getBoundingClientRect())) return box;
  }
  return null;
};

export interface MediaLayoutFlipOptions {
  /** transform を掛ける外箱。ラベル等のオーバーレイごと一緒に動かす。 */
  el: HTMLElement;
  /** レイアウト変更前に captureMediaVisualRect で測った視覚矩形。 */
  firstVisualRect: DOMRect;
  /** FLIP の完了・中止が確定したとき一度だけ呼ばれる（transition 復帰など）。 */
  onSettled?: () => void;
}

/**
 * レイアウト変更後の要素を「旧位置に見える逆変換」で固定してから、
 * transform を identity へ MOVE_EASING で滑空させる（FLIP）。
 *
 * スケールは視覚メディア矩形ベースの等倍（縦横同率）なので、外箱の縦横比が
 * 変わるケース（maxHeight 制約でレターボックス化する等）でも映像は歪まない。
 *
 * 開始を 2 フレーム遅らせるのは、ResizeObserver 経由で反映されるサイズ制約
 * （Preview の mediaMaxHeight など）がレイアウトに乗り切るのを待つため。
 * その間は逆変換により旧位置のまま描画されるので、ちらつきは出ない。
 */
export const runMediaLayoutFlip = ({
  el,
  firstVisualRect,
  onSettled,
}: MediaLayoutFlipOptions): void => {
  // 多重起動（連打）時は前の FLIP を破棄。firstVisualRect は呼び出し側が
  // 破棄前（= 変換が掛かった見た目のまま）に測っている想定なので繋がる。
  disposeActiveFlip?.();

  if (firstVisualRect.width < 1 || firstVisualRect.height < 1) {
    onSettled?.();
    return;
  }

  let rafId = 0;
  let anim: Animation | null = null;
  let disposed = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(rafId);
    anim?.cancel();
    el.style.transform = "";
    el.style.transformOrigin = "";
    el.style.willChange = "";
    if (disposeActiveFlip === dispose) disposeActiveFlip = null;
    onSettled?.();
  };

  // 直近の逆変換パラメータ（アニメーション要否の判定に使う）
  let lastTx = 0;
  let lastTy = 0;
  let lastScale = 1;

  /** 現在レイアウトで旧位置に見える transform を計算して当てる。 */
  const applyInverse = (): boolean => {
    el.style.transform = "none"; // 素のレイアウト矩形を測るため一旦解除
    const boxRect = el.getBoundingClientRect();
    const lastVisual = captureMediaVisualRect(el);
    if (boxRect.width < 1 || lastVisual.width < 1 || lastVisual.height < 1) {
      return false;
    }

    // 視覚メディア矩形を firstVisualRect へ写す等倍スケール + 平行移動。
    // transform-origin は外箱左上（0 0）。
    const s = firstVisualRect.width / lastVisual.width;
    lastScale = s;
    lastTx = firstVisualRect.left - boxRect.left - s * (lastVisual.left - boxRect.left);
    lastTy = firstVisualRect.top - boxRect.top - s * (lastVisual.top - boxRect.top);

    el.style.transformOrigin = "0 0";
    el.style.willChange = "transform";
    el.style.transform = `translate(${lastTx}px, ${lastTy}px) scale(${s})`;
    return true;
  };

  if (!applyInverse()) {
    dispose();
    return;
  }
  disposeActiveFlip = dispose;

  rafId = requestAnimationFrame(() => {
    rafId = requestAnimationFrame(() => {
      if (disposed) return;
      if (!applyInverse()) {
        dispose();
        return;
      }
      // レイアウトがほぼ動かなかった場合はアニメーション不要
      if (Math.abs(lastTx) < 1 && Math.abs(lastTy) < 1 && Math.abs(lastScale - 1) < 0.005) {
        dispose();
        return;
      }

      const inverse = el.style.transform;
      el.style.transform = "";
      anim = el.animate(
        [{ transform: inverse }, { transform: "translate(0px, 0px) scale(1)" }],
        { duration: MOVE_DURATION_MS, easing: MOVE_EASING },
      );
      anim.onfinish = dispose;
      anim.oncancel = dispose;
    });
  });
};
